import { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import {
  ActionTabs,
  FullScreenLoading,
  type ActionTabItem,
  ConfirmDelete,
  NUGGET_VARIANTS,
  type AppIcons,
  REMOTE_PERMISSIONS,
} from '@backrs_ui/common-ui';

import { appStrings as commonAppStrings } from '@backrs_ui/common-ui/src/assets/appStrings';
import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { Header } from '@backrs_ui/grow-ui/src/components/Lubav/Header';
import { MobileViewToggle } from '@backrs_ui/grow-ui/src/components/Lubav/MobileViewToggle';
import { SelectMenu } from '@backrs_ui/grow-ui/src/components/SelectMenu/SelectMenu';
import { sessionNames } from '@backrs_ui/grow-ui/src/components/SolutionSession/SessionStrings';
import { SolutionSessionContent } from '@backrs_ui/grow-ui/src/components/SolutionSession/SolutionSessionContent';
import { useHasPermission } from '@backrs_ui/grow-ui/src/hooks/useHasPermission';
import { useKeyboardDetection } from '@backrs_ui/grow-ui/src/hooks/useKeyboardDetection';
import {
  BoxData,
  isStructuredSession,
  OpenAIModel,
  OPENAI_MODELS,
  SOLUTION_SESSION_IDS,
  SolutionSessionId,
} from '@backrs_ui/grow-ui/src/models/models';
import {
  usePostBookmarkMutation,
  useDeleteBookmarkMutation,
  useGetBookmarksQuery,
} from '@backrs_ui/grow-ui/src/redux/bookmarks/bookmarks.api';
import { resetSolutionSessionBoxData } from '@backrs_ui/grow-ui/src/redux/boxData/boxData';
import {
  setOpenAIModel,
  resetSolutionSession,
} from '@backrs_ui/grow-ui/src/redux/coachLubav/coachLubav';
import { initiateSession } from '@backrs_ui/grow-ui/src/redux/coachLubav/coachLubav.thunk';
import { useAppDispatch, useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import { setActiveSessionType } from '@backrs_ui/grow-ui/src/redux/lubavSessionState/lubavSessionState';
import {
  getSessionState,
  createSessionAction,
  typeGuardForSolutionSessionId,
} from '@backrs_ui/grow-ui/src/redux/lubavSessionState/lubavSessionState.utils';
import {
  resetSolutionSessionState,
  setHasClearedSession,
} from '@backrs_ui/grow-ui/src/redux/solutionSession/solutionSession';
import {
  useGetSessionDataQuery,
  useGetMostRecentSessionQuery,
  useGetMostRecentResumeBuilderSubsessionsQuery,
} from '@backrs_ui/grow-ui/src/redux/solutionSession/solutionSession.api';
import { loadSessionData } from '@backrs_ui/grow-ui/src/redux/solutionSession/solutionSession.thunk';
import { selectUserId } from '@backrs_ui/grow-ui/src/redux/user/user';
import {
  useDeleteAllAcademicHistoryMutation,
  userAcademicHistoryApi,
} from '@backrs_ui/grow-ui/src/redux/userPortfolio/userAcademicHistory.api';
import {
  useDeleteAllEmploymentHistoryMutation,
  userEmploymentHistoryApi,
} from '@backrs_ui/grow-ui/src/redux/userPortfolio/userEmploymentHistory.api';
import {
  useDeleteAllEngagementHistoryMutation,
  userEngagementHistoryApi,
} from '@backrs_ui/grow-ui/src/redux/userPortfolio/userEngagementHistory.api';
import { determineActiveSession } from '@backrs_ui/grow-ui/src/utils/determineActiveSession';

import { Box, SxProps, Theme, useMediaQuery } from '@mui/material';

import { ShareSolutionSessionModal } from './ShareSolutionSessionModal';
import { getBookmarkIdForNugget } from '../Bookmarks/pages/bookmarks.util';
import { StickerCardId, useToolCardById } from '../Home/hooks/stickerCardMapping';
import { useGetTaskActionsAndProperties } from '../Priorities/hooks/tasks.hooks';

const { header } = appStrings.modules;
const { chatBox } = appStrings.modules;

export function SolutionSession() {
  const dispatch = useAppDispatch();
  const location = useLocation();

  const [mobileView, setMobileView] = useState<'chat' | 'info'>('chat');

  const navigate = useNavigate();

  const activeSession = determineActiveSession(location.pathname + location.search);

  useEffect(() => {
    dispatch(setActiveSessionType(activeSession));
  }, [activeSession, dispatch]);

  const userId = useAppSelector(selectUserId);
  const betaMode = useHasPermission(REMOTE_PERMISSIONS.LUBAV_BETA);
  const userLoading = useAppSelector((state) => state.user.documentsLoading);

  const isTablet = useMediaQuery('(max-width: 900px)');
  const isMobile = useMediaQuery('(max-width: 600px)');
  const { keyboardHeight } = useKeyboardDetection({ lockScroll: true, isMobile: isTablet });

  const [showCopySuccess, setShowCopySuccess] = useState(false);
  const [openClearSessionDialog, setOpenClearSessionDialog] = useState(false);
  const [openShareModal, setOpenShareModal] = useState(false);

  // for resume builder forms
  const [deleteAllAcademicHistory] = useDeleteAllAcademicHistoryMutation();
  const [deleteAllEmploymentHistory] = useDeleteAllEmploymentHistoryMutation();
  const [deleteAllEngagementHistory] = useDeleteAllEngagementHistoryMutation();

  const { getStickerCard } = useToolCardById({ navigate });

  const { openToolNuggetAsTask } = useGetTaskActionsAndProperties();

  const { data: bookmarks } = useGetBookmarksQuery(undefined);
  const [postBookmark] = usePostBookmarkMutation();
  const [deleteBookmark] = useDeleteBookmarkMutation();

  // check if we are recalling a session (from path or query param)
  const [searchParams] = useSearchParams();
  const pathParts = location.pathname.split('/');
  const pathSessionId = pathParts[3] ? parseInt(pathParts[3], 10) : null;
  const sessionIdParam = searchParams.get('session_id');
  const querySessionId = sessionIdParam ? parseInt(sessionIdParam, 10) : null;
  const recallSessionId = querySessionId || pathSessionId;

  // get session data if we are recalling a session
  const { data: sessionData, error: sessionRecallError } = useGetSessionDataQuery(
    { session_id: recallSessionId || 0 },
    { skip: !recallSessionId },
  );

  const { hasClearedSession } = useAppSelector(
    (state) => getSessionState(state, activeSession).solutionSession,
  );

  // get most recent session data if we are not recalling a specific session
  const { data: mostRecentSessionData, isLoading: isLoadingMostRecentSession } =
    useGetMostRecentSessionQuery(
      { solution_session: activeSession || '' },
      {
        skip:
          !activeSession ||
          !!recallSessionId ||
          activeSession?.startsWith(SOLUTION_SESSION_IDS.RESUME_BUILDER) ||
          hasClearedSession,
      },
    );

  // get most recent resume builder data for all subsessions
  const { data: mostRecentResumeBuilderData, isLoading: isLoadingMostRecentResumeBuilder } =
    useGetMostRecentResumeBuilderSubsessionsQuery(undefined, {
      skip: !activeSession?.startsWith(SOLUTION_SESSION_IDS.RESUME_BUILDER) || !!recallSessionId,
    });

  const {
    sessionId,
    loading: isTyping,
    streaming: isStreaming,
  } = useAppSelector((state) => getSessionState(state, activeSession).coachLubav);

  const { openAIModel: LLM } = useAppSelector(
    (state) => getSessionState(state, activeSession).coachLubav,
  );

  const { boxData, loading: isLoadingResources } = useAppSelector(
    (state) => getSessionState(state, activeSession).boxData,
  );

  const toggleMobileView = () => {
    setMobileView(mobileView === 'chat' ? 'info' : 'chat');
  };

  const shouldDisableMobileViewToggle = (boxData: BoxData) => {
    // special case for resume builder -- we always allow viewing the output view
    if (activeSession?.startsWith(SOLUTION_SESSION_IDS.RESUME_BUILDER)) {
      return false;
    }

    return !boxData.mainResponse;
  };

  useEffect(() => {
    if (!userId || sessionId || recallSessionId) return;

    // don't initiate if we're clearing the session
    if (hasClearedSession) return;

    // don't initiate if we're still loading data
    if (isLoadingMostRecentSession || isLoadingMostRecentResumeBuilder) return;

    // for resume builder, check if we have data for the current subsession specifically
    const hasCurrentSubsessionData = activeSession?.startsWith(SOLUTION_SESSION_IDS.RESUME_BUILDER)
      ? mostRecentResumeBuilderData?.subsessions?.[activeSession]
      : false;

    // don't initiate if we have a most recent session to load
    if (mostRecentSessionData || hasCurrentSubsessionData) return;

    dispatch(
      initiateSession({
        path: APP_ROUTE.SOLUTION_SESSION,
        solutionSessionName: activeSession,
        activeSession,
      }),
    );
  }, [
    dispatch,
    sessionId,
    userId,
    activeSession,
    recallSessionId,
    mostRecentSessionData,
    isLoadingMostRecentSession,
    mostRecentResumeBuilderData,
    isLoadingMostRecentResumeBuilder,
    hasClearedSession,
  ]);

  // if we are viewing a recalled session, load the data into Redux
  useEffect(() => {
    if (activeSession && recallSessionId) {
      // if there's an error fetching session data, kick the user back to the start of a new session
      if (sessionRecallError) {
        navigate(`${APP_ROUTE.SOLUTION_SESSION.replace(':id?', activeSession)}`);

        return;
      }

      if (sessionData) {
        // if the session type does not match the current session, navigate to correct type WITH session_id
        if (sessionData.session_type !== activeSession) {
          navigate(
            `${APP_ROUTE.SOLUTION_SESSION.replace(':id?', sessionData.session_type)}?session_id=${recallSessionId}`,
          );

          return;
        }

        // Load data into Redux
        dispatch(loadSessionData({ sessionData, activeSession }));
      }
    }
  }, [sessionData, activeSession, recallSessionId, dispatch, navigate, sessionRecallError]);

  if (userLoading) {
    return <FullScreenLoading />;
  }

  if (!activeSession || !typeGuardForSolutionSessionId(activeSession)) {
    navigate(APP_ROUTE.NOT_FOUND_404);

    return null;
  }

  const handleUpdatingOpenAIModel = (updatedOpenAIModel: string) => {
    dispatch(createSessionAction(setOpenAIModel, activeSession)(updatedOpenAIModel as OpenAIModel));
  };

  const handleClearSession = async () => {
    if (!activeSession || !typeGuardForSolutionSessionId(activeSession) || !userId) {
      return;
    }

    // set clearing flag to prevent race conditions
    dispatch(createSessionAction(setHasClearedSession, activeSession)(true));

    // delete all form data & clear cache for resume builder subsessions
    try {
      if (activeSession === 'resume_builder.education') {
        await deleteAllAcademicHistory(undefined).unwrap();
        dispatch(userAcademicHistoryApi.util.resetApiState());
      } else if (activeSession === 'resume_builder.work_experience') {
        await deleteAllEmploymentHistory(undefined).unwrap();
        dispatch(userEmploymentHistoryApi.util.resetApiState());
      } else if (activeSession === 'resume_builder.leadership') {
        await deleteAllEngagementHistory(undefined).unwrap();
        dispatch(userEngagementHistoryApi.util.resetApiState());
      }
    } catch (error) {
      console.error('Error deleting form data:', error);
    }

    // reset messages, session ID, suggested prompts, and pending message state
    dispatch(createSessionAction(resetSolutionSession, activeSession)(undefined));

    // reset output box data
    dispatch(createSessionAction(resetSolutionSessionBoxData, activeSession)(undefined));

    // reset the current input / message state
    dispatch(createSessionAction(resetSolutionSessionState, activeSession)(undefined));

    // and now initiate a new session
    dispatch(
      initiateSession({
        path: APP_ROUTE.SOLUTION_SESSION,
        solutionSessionName: activeSession,
        activeSession,
      }),
    );

    setOpenClearSessionDialog(false);
  };

  const handleStartOverButtonClick = () => {
    setOpenClearSessionDialog(true);
  };

  const leftComponent = (
    <Box sx={middleSectionStyle}>
      {betaMode && !isTablet && (
        <SelectMenu
          id="solution-session-model-select"
          value={LLM}
          onChange={handleUpdatingOpenAIModel}
          title={header.modelTitle}
          options={Object.values(OPENAI_MODELS).map((value) => ({
            value,
            label: header.modelMap[value],
          }))}
        />
      )}
    </Box>
  );

  const stickerCard = getStickerCard(activeSession as StickerCardId);
  const hasTool = !!stickerCard?.tool;

  const bookmarkId = hasTool
    ? getBookmarkIdForNugget(bookmarks, stickerCard?.tool?.id, NUGGET_VARIANTS.TOOL)
    : undefined;

  const handleBookmark = async () => {
    if (!stickerCard?.tool?.id) {
      return;
    }

    if (bookmarkId) {
      await deleteBookmark(bookmarkId);
    } else {
      await postBookmark({
        type: 'tool',
        itemId: stickerCard.tool.id,
      });
    }
  };

  const actions: ActionTabItem[] = hasTool
    ? [
        {
          id: 'solution-session-bookmark-action',
          icon: (bookmarkId ? 'BookmarkCheck' : 'Bookmark') as AppIcons,
          onClick: handleBookmark,
          tooltipHoverText: commonAppStrings.modules.actionTabs.bookmark,
        },
        {
          id: 'solution-session-share-action',
          icon: 'Share06' as const,
          onClick: () => {
            if (sessionId) {
              setOpenShareModal(true);
            } else {
              navigator.clipboard.writeText(window.location.href);
              setShowCopySuccess(true);
            }
          },
          tooltipOnClickText: !sessionId
            ? `Copied link to ${sessionNames[activeSession]}!`
            : undefined,
          tooltipHoverText: commonAppStrings.modules.actionTabs.share,
        },
        {
          id: 'solution-session-add-task-action',
          icon: 'BookmarkAdd' as const,
          tooltipHoverText: commonAppStrings.modules.actionTabs.task,
          onClick: () => {
            if (!sessionId) {
              return;
            }

            openToolNuggetAsTask(stickerCard);
          },
        },
      ]
    : [];

  const actionButtons = (
    <ActionTabs
      actions={actions}
      showCopySuccess={showCopySuccess}
      onCopySuccessChange={setShowCopySuccess}
      borderColor="Colors/Border/border-primary"
    />
  );
  const stickerUrl = stickerCard?.stickerUrl || '/backrs.svg';

  return (
    <Box sx={containerStyle}>
      <Header
        title={sessionNames[activeSession] || ''}
        description={stickerCard?.description}
        stickerUrl={stickerUrl}
        leftComponent={
          isTablet ? (
            <MobileViewToggle
              onClick={toggleMobileView}
              type={mobileView === 'chat' ? 'CHAT' : 'RECS'}
              disabled={shouldDisableMobileViewToggle(boxData)}
            />
          ) : (
            leftComponent
          )
        }
        actionButtons={!isTablet ? actionButtons : null}
        onNewChat={handleStartOverButtonClick}
        newChatDisabled={isTyping || isStreaming || isLoadingResources}
        newChatLabel={isMobile ? chatBox.newChat.mobile : chatBox.startOver}
      />
      <SolutionSessionContent mobileView={mobileView} keyboardHeight={keyboardHeight} />
      <ConfirmDelete
        openDialog={openClearSessionDialog}
        setOpenDialog={setOpenClearSessionDialog}
        headerText={chatBox.startOver}
        bodyText={chatBox.clearSessionBody}
        handleDelete={handleClearSession}
      />
      {sessionId && activeSession && (
        <ShareSolutionSessionModal
          open={openShareModal}
          onClose={() => setOpenShareModal(false)}
          sessionId={sessionId}
          sessionType={activeSession as SolutionSessionId}
        />
      )}
    </Box>
  );
}

// Route entry that redirects migrated sessions to the new /office-hours/<id> route
export function SolutionSessionRouteEntry() {
  const location = useLocation();
  const legacySessionType = location.pathname.match(/^\/solution-session\/([^/]+)/)?.[1];

  if (legacySessionType && isStructuredSession(legacySessionType)) {
    return (
      <Navigate
        to={`${APP_ROUTE.OFFICE_HOURS_SESSION.replace(':id', legacySessionType)}${location.search}`}
        replace
      />
    );
  }

  return <SolutionSession />;
}

const containerStyle: SxProps<Theme> = {
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
  boxSizing: 'border-box',
  maxWidth: '100dvw !important',
};

const middleSectionStyle: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: backrs.spacing['spacing-md'],
  '@media (max-width: 700px)': {
    flexDirection: 'column',
  },
});
